import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Input,
  PageHeader,
  Select,
  StatusChip,
  Tag,
  useToast,
} from '@/design/components'
import { useMe } from '@/app/MeContextProvider'
import { useBreadcrumb } from '@/app/Breadcrumb'
import { dueVariant } from '@/app/dashboard/widgets'
import { ApiError, api } from '@/lib/api'
import { useQuery } from '@tanstack/react-query'
import type { Paginated } from '@/lib/types'
import { EMPTY, formatDate, formatDateTime, formatDueLabel, formatPercent } from '@/lib/format'
import { cn } from '@/lib/cn'
import {
  useDocument,
  useDocumentFile,
  useRetryDocument,
  useToDecretation,
  useVerifyDocument,
} from '../hooks'
import {
  CONFIDENCE_THRESHOLD,
  MONO_FIELD_KEYS,
  SOURCE_LABEL,
  STATUS_LABEL,
  STATUS_VARIANT,
  canRetry,
} from '../labels'
import type { DeadlineHint, DocumentDetail, ExtractionField } from '../types'
import { DocumentPreview } from '../components/DocumentPreview'

/** Kategoria dokumentu do karty „Kategoryzacja" (jak chip na mockupie). */
function categoryOf(docType: string | null): string {
  switch (docType) {
    case 'Postanowienie':
    case 'Wyrok':
    case 'Zawiadomienie':
      return 'Pismo sądowe'
    case 'Pismo urzędowe':
      return 'Pismo urzędowe'
    case 'Umowa':
      return 'Dokument kontraktowy'
    case 'Faktura':
      return 'Dokument księgowy'
    case 'Wezwanie':
      return 'Pismo przedsądowe'
    default:
      return 'Korespondencja'
  }
}

interface CaseOption {
  id: number
  signature: string
  client_name: string
}

/** Sprawy w zakresie widoczności — do ręcznego przypisania („brak dopasowania"). */
function useCaseOptions(enabled: boolean) {
  return useQuery({
    queryKey: ['documents', 'case-options'],
    queryFn: () => api.get<Paginated<CaseOption>>('/cases', { query: { size: 200 } }),
    enabled,
    staleTime: 5 * 60_000,
  })
}

export function VerificationPage() {
  const { documentId } = useParams<{ documentId: string }>()
  const navigate = useNavigate()
  const toast = useToast()
  const { can } = useMe()
  useBreadcrumb(['Obieg dokumentów', 'Skrzynka', 'Weryfikacja'])

  const query = useDocument(documentId)
  const document = query.data
  const file = useDocumentFile(documentId)
  const verify = useVerifyDocument(documentId ?? '')
  const toDecretation = useToDecretation(documentId ?? '')
  const retry = useRetryDocument()

  const [edit, setEdit] = useState(false)
  // Stan edycji indeksujemy POZYCJĄ pola w `extraction.fields`, nie kluczem: model
  // potrafi zwrócić kilka pól o tym samym `key` (np. dwie `party` — powód i pozwany),
  // a słownik po kluczu zlewałby je w jeden wpis i przy zapisie kasował duplikaty.
  const [values, setValues] = useState<string[]>([])
  const [deadlines, setDeadlines] = useState<DeadlineHint[]>([])
  const [caseId, setCaseId] = useState<string>('')
  const [activeIndex, setActiveIndex] = useState<number | null>(null)

  const cases = useCaseOptions(edit)

  useEffect(() => {
    if (!document) return
    const fields = document.extraction?.fields ?? []
    setValues(fields.map((field) => field.value))
    setDeadlines(document.extraction?.deadlines ?? [])
    setCaseId(String(document.case?.id ?? document.suggested_case?.id ?? ''))
  }, [document])

  const fields = document?.extraction?.fields ?? []
  /** Wartość pola po edycji; `values` może być chwilowo krótsze (przeładowanie dokumentu). */
  const valueAt = (index: number) => values[index] ?? fields[index]?.value ?? ''
  const sure = fields.filter((field) => field.confidence >= CONFIDENCE_THRESHOLD).length
  const weakest = useMemo(
    () =>
      fields
        .filter((field) => field.confidence < CONFIDENCE_THRESHOLD)
        .sort((a, b) => a.confidence - b.confidence)[0],
    [fields],
  )

  if (query.isPending) {
    return <p className="text-small text-muted">Wczytywanie dokumentu…</p>
  }
  if (query.isError || !document) {
    const notFound = query.error instanceof ApiError && query.error.isNotFound
    return (
      <EmptyState
        size="page"
        title={notFound ? 'Nie znaleziono dokumentu' : 'Nie udało się wczytać dokumentu'}
        description={
          notFound ? 'Dokument nie istnieje albo jest poza Twoim zakresem widoczności.' : undefined
        }
        action={
          <Button variant="secondary" onClick={() => navigate('/dokumenty/skrzynka')}>
            Wróć do skrzynki
          </Button>
        }
      />
    )
  }

  // Typ dokumentu bierzemy z PIERWSZEGO pola o kluczu `doc_type` (klucze nie są unikalne).
  const docTypeIndex = fields.findIndex((field) => field.key === 'doc_type')
  const docType = docTypeIndex >= 0 ? valueAt(docTypeIndex) : null

  const payload = () => ({
    case_id: caseId ? Number(caseId) : null,
    doc_type: docType ?? document.doc_type ?? undefined,
    delivered_at: document.extraction?.delivered_at ?? null,
    // Wysyłamy KOMPLET pól w oryginalnej kolejności — także duplikaty kluczy, każdy
    // z własną wartością. Backend dopasowuje korekty po kolejności wystąpienia klucza.
    fields: fields.map((field, index) => ({ key: field.key, value: valueAt(index) })),
    deadlines,
  })

  const submit = async (toDecretationToo: boolean) => {
    try {
      await verify.mutateAsync({ ...payload(), to_decretation: toDecretationToo })
      toast.success(
        toDecretationToo ? 'Dane zatwierdzone — dokument w dekretacji' : 'Dane zatwierdzone',
      )
      setEdit(false)
      navigate(toDecretationToo ? '/dokumenty/dekretacja' : '/dokumenty/skrzynka')
    } catch (error) {
      toast.error(error instanceof ApiError ? error.userMessage : 'Nie udało się zapisać danych.')
    }
  }

  const handoff = async () => {
    if (document.status === 'verified') {
      try {
        await toDecretation.mutateAsync()
        toast.success('Dokument przekazany do dekretacji')
        navigate('/dokumenty/dekretacja')
      } catch (error) {
        toast.error(error instanceof ApiError ? error.userMessage : 'Nie udało się przekazać.')
      }
      return
    }
    await submit(true)
  }

  const canVerify = can('documents.verify')
  const signature = document.case?.signature ?? document.suggested_case?.signature ?? null

  return (
    <>
      <PageHeader
        back={{ to: '/dokumenty/skrzynka', label: 'Skrzynka dokumentów' }}
        title={document.title}
        subtitle={
          <span className="flex flex-wrap items-center gap-3">
            <span className="t-mono text-ink">{signature ?? 'brak sygnatury'}</span>
            <span className="text-muted">
              {SOURCE_LABEL[document.source]} · wpływ {formatDateTime(document.received_at)}
            </span>
            <StatusChip variant={STATUS_VARIANT[document.status]}>
              {document.status === 'pending_verification'
                ? 'Oczekuje na weryfikację'
                : STATUS_LABEL[document.status]}
            </StatusChip>
          </span>
        }
        actions={
          canVerify && canRetry(document.status) ? (
            // Dokument w statusie końcowym (awaria pipeline'u albo odrzucenie) nie ma
            // czego zatwierdzać — jedyna sensowna akcja to zawrócenie go do kolejki AI.
            <Button
              variant="primary"
              onClick={() => retry.mutate(document.id)}
              loading={retry.isPending}
            >
              Ponów przetwarzanie
            </Button>
          ) : canVerify ? (
            <>
              <Button variant="secondary" onClick={() => setEdit((current) => !current)}>
                {edit ? 'Zakończ edycję' : 'Popraw dane'}
              </Button>
              <Button
                variant="secondary"
                onClick={handoff}
                loading={toDecretation.isPending}
                disabled={verify.isPending}
              >
                Przekaż do dekretacji
              </Button>
              <Button variant="primary" onClick={() => submit(false)} loading={verify.isPending}>
                Zatwierdź dane
              </Button>
            </>
          ) : null
        }
      />

      <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <DocumentPreview
          url={file.url}
          mime={file.mime}
          error={file.error}
          fields={fields}
          activeIndex={activeIndex}
          ocrQuality={document.extraction?.ocr_quality ?? null}
          fallbackPages={document.pages}
          onDownload={() => {
            if (file.url) window.open(file.url, '_blank', 'noopener')
          }}
        />

        <div className="flex min-w-0 flex-col gap-5">
          <Card>
            <CardHeader
              title="Dane wyciągnięte przez AI"
              action={
                <span className="rounded bg-chip-neutral-bg px-2 py-0.5 text-micro text-chip-neutral-fg">
                  {sure} / {fields.length} pewnych
                </span>
              }
            />
            {fields.length === 0 ? (
              <EmptyState
                title="Brak danych z modelu"
                description="Dokument czeka na przetworzenie albo nie udało się rozpoznać pól."
              />
            ) : (
              <ul className="divide-y divide-line-soft">
                {fields.map((field, index) => (
                  <FieldRow
                    key={`${field.key}-${index}`}
                    field={field}
                    index={index}
                    edit={edit}
                    value={valueAt(index)}
                    onChange={(value) =>
                      setValues((current) => {
                        const next = fields.map((item, i) => current[i] ?? item.value)
                        next[index] = value
                        return next
                      })
                    }
                    onHover={setActiveIndex}
                  />
                ))}
              </ul>
            )}

            {weakest ? (
              <div className="flex gap-2.5 border-t border-line-soft bg-[#fdf8ea] px-5 py-3">
                <span className="mt-[5px] h-[7px] w-[7px] shrink-0 rounded-full bg-warning" />
                <div className="min-w-0">
                  <p className="text-base font-medium text-ink">
                    {weakest.label} — niska pewność ({formatPercent(weakest.confidence)})
                  </p>
                  <p className="mt-0.5 text-small text-muted">
                    Model nie jest pewny wartości „{weakest.value}"
                    {weakest.page ? ` (str. ${weakest.page})` : ''}. Wymaga potwierdzenia przez
                    człowieka.
                  </p>
                </div>
              </div>
            ) : null}
          </Card>

          <Card>
            <CardHeader title="Kategoryzacja" />
            <CardBody>
              <div className="flex flex-wrap items-center gap-2">
                <Tag active>{categoryOf(docType ?? document.doc_type)}</Tag>
                {document.doc_type ? <Tag>{docType ?? document.doc_type}</Tag> : null}
                {signature ? <Tag>{signature}</Tag> : null}
                {document.detected_deadline ? <Tag>Wymaga reakcji</Tag> : null}
              </div>

              {edit ? (
                <div className="mt-4 max-w-[320px]">
                  <Select
                    label="Sprawa"
                    value={caseId}
                    onChange={(event) => setCaseId(event.target.value)}
                    options={[
                      { value: '', label: 'bez przypisania' },
                      ...(cases.data?.items ?? []).map((item) => ({
                        value: String(item.id),
                        label: `${item.signature} — ${item.client_name}`,
                      })),
                    ]}
                  />
                </div>
              ) : (
                <p className="mt-3 text-base text-muted">
                  Sprawa:{' '}
                  {document.case ? (
                    <span className="t-mono text-ink">{document.case.signature}</span>
                  ) : document.suggested_case ? (
                    <span className="t-mono text-ink">
                      {document.suggested_case.signature}{' '}
                      <span className="text-muted">(propozycja AI)</span>
                    </span>
                  ) : (
                    <span className="text-danger">brak dopasowania</span>
                  )}
                </p>
              )}

              <p className="mt-3 text-base text-muted">
                Zaproponowana ścieżka obiegu:{' '}
                <span className="font-semibold text-ink">
                  Sekretariat → dekretacja partnera → prawnik prowadzący
                </span>
              </p>
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Wykryte terminy"
              action={
                edit ? (
                  <button
                    type="button"
                    onClick={() =>
                      setDeadlines((current) => [
                        ...current,
                        {
                          due_date: new Date().toISOString().slice(0, 10),
                          title: 'Termin procesowy',
                          legal_basis: null,
                          days: null,
                          confidence: 1,
                          source_page: null,
                        },
                      ])
                    }
                    className="text-micro text-muted transition-colors hover:text-ink"
                  >
                    + Dodaj termin
                  </button>
                ) : null
              }
            />
            {deadlines.length === 0 ? (
              <EmptyState
                title="Model nie wykrył terminu"
                description="Termin możesz dodać ręcznie przed zatwierdzeniem."
              />
            ) : (
              <ul className="divide-y divide-line-soft">
                {deadlines.map((deadline, index) => (
                  <li key={`${deadline.due_date}-${index}`} className="px-5 py-3.5">
                    {edit ? (
                      <div className="flex flex-wrap items-end gap-2">
                        <Input
                          block={false}
                          type="date"
                          label="Termin"
                          value={deadline.due_date}
                          onChange={(event) =>
                            setDeadlines((current) =>
                              current.map((item, i) =>
                                i === index ? { ...item, due_date: event.target.value } : item,
                              ),
                            )
                          }
                        />
                        <Input
                          label="Opis"
                          className="min-w-[180px]"
                          value={deadline.title}
                          onChange={(event) =>
                            setDeadlines((current) =>
                              current.map((item, i) =>
                                i === index ? { ...item, title: event.target.value } : item,
                              ),
                            )
                          }
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setDeadlines((current) => current.filter((_, i) => i !== index))
                          }
                        >
                          Usuń
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-base font-semibold text-ink">
                            {deadline.title}
                          </p>
                          <p className="mt-0.5 text-small text-muted">
                            {formatDate(deadline.due_date)}
                            {deadline.legal_basis ? ` · ${deadline.legal_basis}` : ''}
                            {deadline.days ? ` · ${deadline.days} dni` : ''}
                          </p>
                        </div>
                        <StatusChip variant={dueVariant(deadline.due_date)} size="sm">
                          {formatDueLabel(deadline.due_date)}
                        </StatusChip>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <DocumentMeta document={document} />
        </div>
      </div>
    </>
  )
}

/**
 * Model bywa zwraca literalny string „null" zamiast pustej wartości (widoczne na
 * produkcji: „sygnatura akt: null"). Traktujemy go jak brak — inaczej klient czyta
 * w polu słowo „null" z pewnością 100%.
 */
function displayValue(value: string): string {
  const trimmed = value.trim()
  return !trimmed || trimmed.toLowerCase() === 'null' ? EMPTY : value
}

function FieldRow({
  field,
  index,
  edit,
  value,
  onChange,
  onHover,
}: {
  field: ExtractionField
  /** Pozycja pola w `extraction.fields` — tożsamość wiersza (klucze bywają zduplikowane). */
  index: number
  edit: boolean
  value: string
  onChange: (value: string) => void
  onHover: (index: number | null) => void
}) {
  const sure = field.confidence >= CONFIDENCE_THRESHOLD
  return (
    <li
      className="flex items-start gap-3 px-5 py-3"
      onMouseEnter={() => onHover(index)}
      onMouseLeave={() => onHover(null)}
    >
      <span className="w-[128px] shrink-0 pt-1 text-base text-muted">{field.label}</span>
      <div className="min-w-0 flex-1">
        {edit ? (
          <Input value={value} onChange={(event) => onChange(event.target.value)} mono={MONO_FIELD_KEYS.has(field.key)} />
        ) : (
          <p
            className={cn(
              'break-words text-base text-ink',
              MONO_FIELD_KEYS.has(field.key) ? 't-mono' : 'font-semibold',
            )}
          >
            {displayValue(value)}
          </p>
        )}
        <p className="mt-1 text-micro text-muted">
          {field.page ? `str. ${field.page}` : 'str. —'} · {formatPercent(field.confidence)}
          {field.corrected ? ' · poprawione' : ''}
        </p>
      </div>
      <span
        title={sure ? 'Pewność powyżej progu' : 'Wymaga potwierdzenia'}
        className={cn('mt-2 h-[7px] w-[7px] shrink-0 rounded-full', sure ? 'bg-success' : 'bg-warning')}
      />
    </li>
  )
}

function DocumentMeta({ document }: { document: DocumentDetail }) {
  return (
    <Card>
      <CardHeader title="Metryka pliku" />
      <CardBody className="grid grid-cols-2 gap-y-2 text-base">
        <span className="text-muted">Plik</span>
        <span className="truncate text-ink">{document.original_filename}</span>
        <span className="text-muted">Rozmiar</span>
        <span className="text-ink">{(document.size_bytes / 1024).toFixed(0)} kB</span>
        <span className="text-muted">Model</span>
        <span className="text-ink">
          {document.extraction?.provider ?? EMPTY}
          {document.extraction?.model_version ? ` · ${document.extraction.model_version}` : ''}
        </span>
        <span className="text-muted">Zweryfikował</span>
        <span className="text-ink">
          {document.verified_by
            ? `${document.verified_by.full_name} · ${formatDate(document.verified_at)}`
            : EMPTY}
        </span>
      </CardBody>
    </Card>
  )
}
